import { getRegionByCode } from '@luxuryescapes/lib-regions';

import { CommissionRule } from '~/types/services/agentHub';

export type CommissionsConditionTypes =
  | 'productType'
  | 'agency'
  | 'agent'
  | 'location'
  | 'vendor'
  | 'cruiseLine'
  | 'offer'
  | 'margin'
  | 'tier'
  | 'startDate'
  | 'endDate'
  | 'flightDepartureDate'
  | 'flightReturnDate'
  | 'flightCarrier'
  | 'flightDeparture'
  | 'flightArrival'
  | 'flightClass'
  | 'tag'
  | 'bundledWithOffer'
  | 'bundledWithTypes'
  | 'bundleTotalPrice'
  | 'minTotalSpend'
  | 'affiliation'
  | 'provider';

export type CommissionOption = Array<{
  value: string | number;
  label: string;
}>;

export type RuleConditionType = [keyof RuleConditions, CommissionOption];

export type RuleConditions = {
  [key in CommissionsConditionTypes]: CommissionOption;
};

export interface RuleCondition {
  type: keyof RuleConditions;
  value: CommissionOption;
}

export interface CommissionFormData {
  description: string;
  startDate: string;
  endDate: string;
  regions: Array<string>;
  discountType: string;
  discountAmount: number;
  /** Base commissions only; nullable */
  priority?: number | null;
  /** Base commissions only */
  canHaveAdditionalCommissions?: boolean;
}

export const INITIAL_COMMISSION_CONDITIONS: RuleConditions = {
  agency: [],
  agent: [],
  offer: [],
  vendor: [],
  cruiseLine: [],
  provider: [],
  location: [],
  flightDeparture: [],
  flightArrival: [],
  flightDepartureDate: [],
  flightReturnDate: [],
  bundledWithOffer: [],
  flightClass: [],
  productType: [],
  flightCarrier: [],
  margin: [],
  bundleTotalPrice: [],
  minTotalSpend: [],
  tier: [],
  startDate: [],
  endDate: [],
  bundledWithTypes: [],
  tag: [],
  affiliation: [],
};

export const ruleConditionsToTextMap = new Map<keyof RuleConditions, string>([
  ['agency', 'Agencies'],
  ['agent', 'Agents'],
  ['offer', 'Offers'],
  ['vendor', 'Vendors'],
  ['cruiseLine', 'Cruise lines'],
  ['provider', 'Providers'],
  ['location', 'Locations'],
  ['flightDeparture', 'Departure location'],
  ['flightArrival', 'Arrival location'],
  ['flightDepartureDate', 'Flight departure date'],
  ['flightReturnDate', 'Flight return date'],
  ['productType', 'Product type'],
  ['flightCarrier', 'Flight carriers'],
  ['bundledWithOffer', 'Bundled with offer'],
  ['bundledWithTypes', 'Bundled with types'],
  ['bundleTotalPrice', 'Bundle total price'],
  ['minTotalSpend', 'Total spend'],
  ['flightClass', 'Flight class'],
  ['margin', 'Margin'],
  ['tier', 'Tier'],
  ['startDate', 'Start date'],
  ['endDate', 'End date'],
  ['tag', 'User tags'],
  ['affiliation', 'Affiliations'],
]);

export const COMMISSION_RULE_CONDITION_TYPES: Array<CommissionsConditionTypes> = [
  'location',
  'flightDeparture',
  'flightArrival',
  'flightDepartureDate',
  'flightReturnDate',
  'vendor',
  'cruiseLine',
  'provider',
  'agency',
  'agent',
  'tag',
  'productType',
  'flightCarrier',
  'bundledWithOffer',
  'flightClass',
  'bundledWithTypes',
  'bundleTotalPrice',
  'minTotalSpend',
  'affiliation',
  'offer',
  'margin',
  'tier',
  'startDate',
  'endDate',
];

export type CommissionRuleConditionDescription = {
  description: string;
  appliesTo: string;
};

export const COMMISSION_RULE_CONDITION_DESCRIPTIONS: Record<
  CommissionsConditionTypes,
  CommissionRuleConditionDescription
> = {
  location: {
    description:
      'Matches destination tokens on cart items. Select multiple values to match any selected location. This is separate from top-level Regions.',
    appliesTo:
      'Products whose customer payload includes location tokens, including accommodation, flights, tours, cruises, and experiences. Some cart lines, such as transfers and car hire, may not provide location tokens.',
  },
  flightDeparture: {
    description: 'Matches a flight departure city or country. Select multiple values to match any selected departure.',
    appliesTo: 'Flights only.',
  },
  flightArrival: {
    description: 'Matches a flight arrival city or country. Select multiple values to match any selected arrival.',
    appliesTo: 'Flights only.',
  },
  flightDepartureDate: {
    description:
      'Matches standalone flights only when the departure date falls inside the selected start and end dates (inclusive). Does not apply to bundled or package flights. If matched, the commission applies to the entire flight item, including all legs.',
    appliesTo: 'Standalone flights only.',
  },
  flightReturnDate: {
    description:
      'Matches standalone flights only when the return date falls inside the selected start and end dates (inclusive). Does not apply to bundled or package flights. If matched, the commission applies to the entire flight item, including all legs.',
    appliesTo: 'Standalone flights only.',
  },
  vendor: {
    description:
      'Matches items from specific vendor IDs or vendor names. Select multiple values to match any selected vendor.',
    appliesTo:
      'Any product that sends a vendor value, including flights, cruises, hotels, villas, car hire, and offer-page experiences. Some cart lines send an empty vendor.',
  },
  cruiseLine: {
    description: 'Matches cruise items from specific cruise lines. Select multiple values to match any selected line.',
    appliesTo: 'Cruises only.',
  },
  agency: {
    description:
      'Applies to agents that belong to the selected agencies. Select multiple values to match any selected agency.',
    appliesTo: 'Agent accounts only.',
  },
  agent: {
    description: 'Applies to specific agent user IDs. Select multiple values to match any selected agent.',
    appliesTo: 'Agent accounts only.',
  },
  productType: {
    description:
      'Matches specific product types from the available list (for example hotel, flight, or experience). Select multiple values to match any selected type.',
    appliesTo: 'All products. If no product type is selected, all product types are considered.',
  },
  bundledWithOffer: {
    description: 'Matches bundle relationships by offer ID.',
    appliesTo:
      'Mainly flight + accommodation bundles. Customer cart payloads send bundled offer IDs on flight lines linked to accommodation; standalone items do not send this list.',
  },
  offer: {
    description: 'Matches specific offer IDs. Select multiple values to match any selected offer.',
    appliesTo: 'Any product with a matching offer ID or journey ID.',
  },
  margin: {
    description: 'Matches items whose encrypted margin is present and within Min and Max (inclusive).',
    appliesTo:
      'Bedbank, LPC/LE hotel packages, villas, and some package-backed legacy offers. Flights, cruises, Tour V2, experiences, transfers, and car hire do not currently send margin.',
  },
  tier: {
    description: 'Applies to agents in the selected tiers. Select multiple values to match any selected tier.',
    appliesTo: 'Agent accounts only.',
  },
  startDate: {
    description: 'Matches item travel start dates against the selected date.',
    appliesTo:
      'Accommodation, villas, Tour V2, cruises, experiences, transfers, and tour extras in cart payloads. Flights and car hire do not send cart dates; offer lookups use catalog ranges for Tour V2/cruise and selected stay dates for hotels.',
  },
  endDate: {
    description: 'Matches item travel end dates against the selected date.',
    appliesTo:
      'Accommodation, villas, Tour V2, cruises, experiences, transfers, and tour extras in cart payloads. Flights and car hire do not send cart dates; offer lookups use catalog ranges for Tour V2/cruise and selected stay dates for hotels.',
  },
  flightClass: {
    description: 'Matches the selected flight cabin class values. Select multiple values to match any selected class.',
    appliesTo: 'Flights only.',
  },
  bundledWithTypes: {
    description:
      'Matches resolved bundles that contain exactly the selected product types. At least two types are required.',
    appliesTo:
      'Resolved bundle contexts, mainly flight + accommodation. Offer pages can also synthesize flight bundle context for accommodation.',
  },
  bundleTotalPrice: {
    description: 'Matches bundled items when the resolved bundle total is within Min and Max (inclusive).',
    appliesTo: 'Detected bundles, usually flight + accommodation. Standalone items do not have a bundle total.',
  },
  flightCarrier: {
    description:
      'Matches the flight marketing carrier IATA code. Select multiple values to match any selected carrier.',
    appliesTo: 'Flights only.',
  },
  minTotalSpend: {
    description: 'Matches the commission cart total against the selected spend value.',
    appliesTo: 'Cart commission calculations across all commissionable line prices.',
  },
  tag: {
    description: 'Applies when the user has at least one selected user tag (for example re-engagement tags).',
    appliesTo: 'Agent accounts only.',
  },
  affiliation: {
    description: 'Applies to agents that belong to the selected affiliations.',
    appliesTo: 'Agent accounts only.',
  },
  provider: {
    description: 'Matches items from specific provider keys.',
    appliesTo: 'Products that send a provider value, such as flights or selected integrations.',
  },
};

export const PROVIDER_VERTICALS = ['experiences'] as const;
export type ProviderVertical = (typeof PROVIDER_VERTICALS)[number];

export const PROVIDER_OPTIONS_BY_VERTICAL: Record<ProviderVertical, ReadonlyArray<{ value: string; label: string }>> = {
  experiences: [
    { value: 'rez', label: 'Rezdy' },
    { value: 'led', label: 'LED' },
    { value: 'dby', label: 'Derbysoft' },
    { value: 'asp', label: 'Aspire' },
    { value: 'klo', label: 'Klook' },
    { value: 'cln', label: 'Collinson' },
    { value: 'cmc', label: 'CustomLinc' },
    { value: 'hby', label: 'Hubby' },
    { value: 'ing', label: 'Ingresso' },
  ],
};

export const USER_TAG_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'no_booking_last_6_months', label: 'No booking in last 6 months' },
  { value: 'no_booking_last_12_months', label: 'No booking in last 12 months' },
];

export const PRODUCT_TYPES_OPTIONS = [
  'addon',
  'always_on',
  'bedbank_hotel',
  'bundle_and_save',
  'car_hire',
  'connection_tour',
  'corporate',
  'cruise',
  'cruise-exclusive',
  'cruise-flash',
  'cruise-tactical',
  'direct_tour',
  'experience',
  'flight',
  'flash',
  'hotel',
  'last_minute_hotel',
  'limited_time_special',
  'partner_tour',
  'rental',
  'tactical_ao_hotel',
  'tour',
  'transfer',
  'ultra_lux_tour',
  'ultralux_hotel',
  'insider_series_tour',
];

export const TIER_OPTIONS: CommissionRule[0]['tier'] = ['tier1', 'tier2', 'tier3'];

export const AVAILABLE_REGIONS = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
];

export const AVAILABLE_REGIONS_LIB_REGIONS = AVAILABLE_REGIONS.map((region) => getRegionByCode(region.value));

export const AGENT_HUB_BRANDS = {
  leagenthub: 'LE Agent Hub',
  leagenthub_cvfr: 'CVFR Holidays',
} as const;

export type AgentHubBrand = keyof typeof AGENT_HUB_BRANDS;

export function ensureAgentHubBrands(brand: string): AgentHubBrand {
  if (Object.keys(AGENT_HUB_BRANDS).includes(brand)) {
    return brand as AgentHubBrand;
  }
  throw new Error(`Invalid agent hub brand: ${brand}`);
}

export function labelForUserTagValue(value: string) {
  return USER_TAG_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** svc-auth role slugs returned on auth users (svc-agent Roles enum matches these strings). */
export const AGENT_HUB_AUTH_ROLE_AGENCY_ADMIN = 'agency-admin';
export const AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN = 'affiliation-admin';
